import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Users,
  Filter,
  Copy,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
  X,
  AlertCircle,
  Check,
  Eye,
  ExternalLink,
  Phone,
  Building2,
  Tag,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Badge, Spinner, Modal, useToast } from '@centraoneplus/ui';
import { customerService, CustomerSegment, SegmentRule, SegmentCondition, Customer } from '@services/customer.service';
import { sanitizeInteger } from '@/lib/numberInput';
import { ticketTypesService } from '@services/ticket-types.service';
import { pipelineService } from '@services/pipeline.service';
import { teamsService } from '@services/teams.service';
import { teamMembersService } from '@services/team-members.service';
import { slaService } from '@services/sla.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Available fields for segmentation
const SEGMENT_FIELDS = [
  { value: 'email', label: 'Email', type: 'string' },
  { value: 'name', label: 'Name', type: 'string' },
  { value: 'phone', label: 'Phone', type: 'string' },
  { value: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'blocked'] },
  { value: 'source', label: 'Source', type: 'string' },
  { value: 'createdAt', label: 'Created Date', type: 'date' },
  { value: 'lastSeenAt', label: 'Last Seen', type: 'date' },
  { value: 'organizationId', label: 'Organization', type: 'string' },
  { value: 'timezone', label: 'Timezone', type: 'string' },
  { value: 'locale', label: 'Language', type: 'string' },
];

// Ticket-scoped fields — "customer has at least one ticket where <field> ...".
// The `ticket.` prefix tells the backend to resolve these via a ticket lookup
// (there is no Customer→Ticket relation). Static-option fields use `type:'select'`;
// id-reference fields use `type:'ref'` and load their options live (see refOptions).
const TICKET_STATUS_OPTIONS = ['new', 'in_progress', 'pending', 'resolved', 'closed'];
const TICKET_PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];
const TICKET_CHANNEL_OPTIONS = ['email', 'chat', 'phone', 'web', 'api', 'whatsapp', 'facebook', 'twitter'];

const TICKET_FIELDS = [
  { value: 'ticket.status', label: 'Ticket Status', type: 'select', options: TICKET_STATUS_OPTIONS },
  { value: 'ticket.priority', label: 'Ticket Priority', type: 'select', options: TICKET_PRIORITY_OPTIONS },
  { value: 'ticket.channel', label: 'Ticket Channel', type: 'select', options: TICKET_CHANNEL_OPTIONS },
  { value: 'ticket.ticketTypeId', label: 'Ticket Type', type: 'ref', source: 'ticketTypes' },
  { value: 'ticket.pipelineId', label: 'Pipeline', type: 'ref', source: 'pipelines' },
  { value: 'ticket.assigneeId', label: 'Assigned Agent', type: 'ref', source: 'agents' },
  { value: 'ticket.teamId', label: 'Team', type: 'ref', source: 'teams' },
  { value: 'ticket.slaPolicyId', label: 'SLA Policy', type: 'ref', source: 'slaPolicies' },
  { value: 'ticket.tags', label: 'Ticket Tag', type: 'ticketTags' },
];

// All selectable fields (customer columns + ticket-scoped), used for lookups.
const ALL_FIELDS = [...SEGMENT_FIELDS, ...TICKET_FIELDS];

// Operators based on field type
const OPERATORS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  string: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'contains', label: 'contains' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'ends_with', label: 'ends with' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  number: [
    { value: 'equals', label: 'equals' },
    { value: 'not_equals', label: 'does not equal' },
    { value: 'greater_than', label: 'is greater than' },
    { value: 'less_than', label: 'is less than' },
  ],
  date: [
    { value: 'equals', label: 'is on' },
    { value: 'greater_than', label: 'is after' },
    { value: 'less_than', label: 'is before' },
    { value: 'days_ago', label: 'in the last X days' },
    { value: 'is_empty', label: 'is not set' },
  ],
  select: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
    { value: 'in', label: 'is any of' },
    { value: 'not_in', label: 'is none of' },
  ],
  array: [
    { value: 'contains', label: 'includes' },
    { value: 'not_contains', label: 'does not include' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  // Ticket id-reference fields (type/pipeline/agent/team/SLA): single-value match.
  ref: [
    { value: 'equals', label: 'is' },
    { value: 'not_equals', label: 'is not' },
  ],
  // Ticket tags: has / doesn't have the tag.
  ticketTags: [
    { value: 'contains', label: 'includes' },
    { value: 'not_contains', label: 'excludes' },
  ],
};

// Reference-option lists loaded live for `type:'ref'` ticket fields.
type RefOptions = Record<string, { value: string; label: string }[]>;

// Page size for the static-segment "Add customers" picker (API caps at 100/request).
const CUSTOMER_PICKER_PAGE = 100;

// Segment colors
const SEGMENT_COLORS = [
  { value: 'blue', label: 'Blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  { value: 'green', label: 'Green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  { value: 'red', label: 'Red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  { value: 'purple', label: 'Purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { value: 'pink', label: 'Pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
  { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  { value: 'gray', label: 'Gray', bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-200', border: 'border-gray-300 dark:border-gray-700' },
];

// Condition row component
function ConditionRow({
  condition,
  onChange,
  onRemove,
  isLast,
  refOptions,
}: {
  condition: SegmentCondition;
  onChange: (updated: SegmentCondition) => void;
  onRemove: () => void;
  isLast: boolean;
  refOptions: RefOptions;
}) {
  const fieldConfig = ALL_FIELDS.find(f => f.value === condition.field) as
    | { value: string; label: string; type: string; options?: string[]; source?: string }
    | undefined;
  const operators = fieldConfig ? OPERATORS_BY_TYPE[fieldConfig.type] || OPERATORS_BY_TYPE.string : OPERATORS_BY_TYPE.string;
  const needsValue = !['is_empty', 'is_not_empty'].includes(condition.operator);

  return (
    <div className="flex items-center gap-2 py-2">
      {/* Field selector */}
      <ThemedSelect
        value={condition.field}
        onChange={(e) => onChange({ ...condition, field: e.target.value, value: '' })}
        className="w-40 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
      >
        <option value="">Select field</option>
        <optgroup label="Customer">
          {SEGMENT_FIELDS.map(field => (
            <option key={field.value} value={field.value}>{field.label}</option>
          ))}
        </optgroup>
        <optgroup label="Ticket">
          {TICKET_FIELDS.map(field => (
            <option key={field.value} value={field.value}>{field.label}</option>
          ))}
        </optgroup>
      </ThemedSelect>

      {/* Operator selector */}
      <ThemedSelect
        value={condition.operator}
        onChange={(e) => onChange({ ...condition, operator: e.target.value as SegmentCondition['operator'] })}
        className="w-44 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
      >
        {operators.map(op => (
          <option key={op.value} value={op.value}>{op.label}</option>
        ))}
      </ThemedSelect>

      {/* Value input */}
      {needsValue && (
        fieldConfig?.type === 'select' && fieldConfig.options ? (
          <ThemedSelect
            value={condition.value || ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select value</option>
            {fieldConfig.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </ThemedSelect>
        ) : fieldConfig?.type === 'number' ? (
          <input
            type="text"
            inputMode="numeric"
            value={condition.value || ''}
            onChange={(e) => onChange({ ...condition, value: sanitizeInteger(e.target.value) })}
            placeholder="Enter value"
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        ) : fieldConfig?.type === 'date' ? (
          condition.operator === 'days_ago' ? (
            <input
              type="text"
              inputMode="numeric"
              value={condition.value || ''}
              onChange={(e) => onChange({ ...condition, value: sanitizeInteger(e.target.value) })}
              placeholder="Number of days"
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          ) : (
            <input
              type="date"
              value={condition.value || ''}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          )
        ) : fieldConfig?.type === 'ref' ? (
          <ThemedSelect
            value={condition.value || ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="">
              {refOptions[fieldConfig.source!]?.length ? 'Select value' : 'Loading…'}
            </option>
            {(refOptions[fieldConfig.source!] || []).map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </ThemedSelect>
        ) : (
          <input
            type="text"
            value={condition.value || ''}
            onChange={(e) => onChange({ ...condition, value: e.target.value })}
            placeholder={fieldConfig?.type === 'ticketTags' ? 'Enter tag' : 'Enter value'}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        )
      )}

      {/* Remove button */}
      {!isLast && (
        <button
          onClick={onRemove}
          className="p-2 text-gray-400 hover:text-red-500 transition-colors dark:text-gray-500"
          title="Remove condition"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// Rule group component
function RuleGroup({
  rule,
  onChange,
  onRemove,
  canRemove,
  refOptions,
}: {
  rule: SegmentRule;
  onChange: (updated: SegmentRule) => void;
  onRemove: () => void;
  canRemove: boolean;
  refOptions: RefOptions;
}) {
  const addCondition = () => {
    const newCondition: SegmentCondition = {
      field: '',
      operator: 'equals',
      value: '',
    };
    onChange({
      ...rule,
      conditions: [...rule.conditions, newCondition],
    });
  };

  const updateCondition = (index: number, updated: SegmentCondition) => {
    const conditions = [...rule.conditions];
    conditions[index] = updated;
    onChange({ ...rule, conditions });
  };

  const removeCondition = (index: number) => {
    if (rule.conditions.length > 1) {
      onChange({
        ...rule,
        conditions: rule.conditions.filter((_, i) => i !== index),
      });
    }
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Match</span>
          <ThemedSelect
            value={rule.logicalOperator}
            onChange={(e) => onChange({ ...rule, logicalOperator: e.target.value as 'AND' | 'OR' })}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="AND">ALL</option>
            <option value="OR">ANY</option>
          </ThemedSelect>
          <span className="text-sm text-gray-600 dark:text-gray-400">of the following conditions</span>
        </div>
        {canRemove && (
          <button
            onClick={onRemove}
            className="p-1 text-gray-400 hover:text-red-500 transition-colors dark:text-gray-500"
            title="Remove rule group"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="space-y-1">
        {rule.conditions.map((condition, index) => (
          <div key={index}>
            <ConditionRow
              condition={condition}
              onChange={(updated) => updateCondition(index, updated)}
              onRemove={() => removeCondition(index)}
              isLast={rule.conditions.length === 1}
              refOptions={refOptions}
            />
            {index < rule.conditions.length - 1 && (
              <div className="flex items-center gap-2 pl-2 py-1">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  {rule.logicalOperator}
                </span>
                <div className="flex-1 border-t border-dashed border-gray-300 dark:border-gray-600" />
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        onClick={addCondition}
        className="mt-3 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
      >
        <Plus className="h-4 w-4" />
        Add condition
      </button>
    </div>
  );
}

// Main component
export default function CustomerSegmentsBuilder() {
  const toast = useToast();
  const navigate = useNavigate();
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'dynamic' | 'static'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<CustomerSegment | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingSegment, setDeletingSegment] = useState<CustomerSegment | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  // Live option lists for ticket ref fields (type/pipeline/agent/team/SLA).
  const [refOptions, setRefOptions] = useState<RefOptions>({});
  // "View members" modal — the customers belonging to a segment.
  const [membersSegment, setMembersSegment] = useState<CustomerSegment | null>(null);
  const [members, setMembers] = useState<Customer[]>([]);
  const [membersTotal, setMembersTotal] = useState(0);
  const [membersPage, setMembersPage] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);
  // Add-customers flow (static segments only).
  const [showAddCustomers, setShowAddCustomers] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedToAdd, setSelectedToAdd] = useState<Set<string>>(new Set());
  const [isMutatingMembers, setIsMutatingMembers] = useState(false);
  // Existing member ids (loaded when the picker opens) so already-members are
  // hidden from search across all pages, not just the current one.
  const [existingMemberIds, setExistingMemberIds] = useState<Set<string>>(new Set());

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'dynamic' as 'dynamic' | 'static',
    color: 'blue',
    rules: [] as SegmentRule[],
  });

  // Load segments
  const loadSegments = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await customerService.getSegments(
        filterType !== 'all' ? { type: filterType } : undefined
      );
      setSegments(data);
    } catch (error) {
      console.error('Error loading segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setIsLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  // Load the live option lists for ticket ref fields when the editor opens.
  // Each source fails independently (falls back to []) so one outage doesn't
  // block the others. Loaded once per modal open.
  useEffect(() => {
    if (!showModal || Object.keys(refOptions).length > 0) return;
    let cancelled = false;
    (async () => {
      const [types, pipelines, teams, members, slas] = await Promise.all([
        ticketTypesService.getTicketTypes({ active: true }).catch(() => []),
        pipelineService.getPipelines().catch(() => []),
        teamsService.getTeams().catch(() => ({ data: [] as any[] })),
        teamMembersService.getTeamMembers().catch(() => ({ data: [] as any[] })),
        slaService.getPolicies().catch(() => []),
      ]);
      if (cancelled) return;
      const agentLabel = (m: any) =>
        `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email || m.id;
      setRefOptions({
        ticketTypes: (types || []).map((t: any) => ({ value: t.id, label: t.name })),
        pipelines: (pipelines || []).map((p: any) => ({ value: p.id, label: p.name })),
        teams: ((teams as any)?.data || []).map((t: any) => ({ value: t.id, label: t.name })),
        agents: ((members as any)?.data || []).map((m: any) => ({ value: m.id, label: agentLabel(m) })),
        slaPolicies: (slas || []).map((s: any) => ({ value: s.id, label: s.name })),
      });
    })();
    return () => { cancelled = true; };
  }, [showModal, refOptions]);

  // Debounced customer search for the static-segment "Add customers" picker.
  useEffect(() => {
    if (!showAddCustomers) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await customerService.getCustomers({
          search: customerSearch.trim() || undefined,
          page: 1,
          limit: CUSTOMER_PICKER_PAGE, // API caps at 100/request; "Load more" pages the rest
        });
        if (!cancelled) {
          setSearchResults(res.data || []);
          setSearchTotal(res.total || 0);
          setSearchPage(1);
        }
      } catch (err) {
        // Never fail silently: a swallowed error here looks identical to "no
        // matching customers", so a real request failure reads as the search
        // doing nothing. Surface it (and log) instead of blanking quietly.
        if (!cancelled) {
          console.error('Customer picker search failed:', err);
          setSearchResults([]);
          setSearchTotal(0);
          toast.error('Customer search failed', 'Could not load customers. Please try again.');
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [customerSearch, showAddCustomers]);

  // Load the next page of customers into the picker (all customers reachable).
  const loadMoreCustomers = async () => {
    try {
      setLoadingMore(true);
      const nextPage = searchPage + 1;
      const res = await customerService.getCustomers({
        search: customerSearch.trim() || undefined,
        page: nextPage,
        limit: CUSTOMER_PICKER_PAGE,
      });
      setSearchResults((prev) => {
        const seen = new Set(prev.map((c) => c.id));
        return [...prev, ...(res.data || []).filter((c) => !seen.has(c.id))];
      });
      setSearchTotal(res.total || searchTotal);
      setSearchPage(nextPage);
    } catch (error) {
      console.error('Error loading more customers:', error);
      toast.error('Failed to load more customers');
    } finally {
      setLoadingMore(false);
    }
  };

  // Load the segment's existing member ids when the picker opens, so the search
  // can hide already-members across all pages (backend also skips duplicates).
  useEffect(() => {
    if (!showAddCustomers || !membersSegment) {
      setExistingMemberIds(new Set());
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await customerService.getSegmentMembers(membersSegment.id, { limit: 100, offset: 0 });
        if (!cancelled) setExistingMemberIds(new Set((data.customers || []).map((c) => c.id)));
      } catch {
        /* non-fatal: fall back to current-page exclusion */
      }
    })();
    return () => { cancelled = true; };
  }, [showAddCustomers, membersSegment]);

  // Filter segments by search
  const filteredSegments = segments.filter(segment =>
    segment.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    segment.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Open create modal
  const handleCreate = () => {
    setEditingSegment(null);
    setFormData({
      name: '',
      description: '',
      type: 'dynamic',
      color: 'blue',
      rules: [{
        id: crypto.randomUUID(),
        conditions: [{ field: '', operator: 'equals', value: '' }],
        logicalOperator: 'AND',
      }],
    });
    setPreviewCount(null);
    setShowModal(true);
  };

  // Open edit modal
  const handleEdit = (segment: CustomerSegment) => {
    setEditingSegment(segment);
    setFormData({
      name: segment.name,
      description: segment.description || '',
      type: segment.type,
      color: segment.color || 'blue',
      rules: segment.rules?.length ? segment.rules : [{
        id: crypto.randomUUID(),
        conditions: [{ field: '', operator: 'equals', value: '' }],
        logicalOperator: 'AND',
      }],
    });
    setPreviewCount(segment.customerCount);
    setShowModal(true);
  };

  // Duplicate segment
  const handleDuplicate = (segment: CustomerSegment) => {
    setEditingSegment(null);
    setFormData({
      name: `${segment.name} (Copy)`,
      description: segment.description || '',
      type: segment.type,
      color: segment.color || 'blue',
      rules: segment.rules?.length ? segment.rules : [{
        id: crypto.randomUUID(),
        conditions: [{ field: '', operator: 'equals', value: '' }],
        logicalOperator: 'AND',
      }],
    });
    setPreviewCount(null);
    setShowModal(true);
  };

  // Add rule group
  const addRuleGroup = () => {
    setFormData({
      ...formData,
      rules: [...formData.rules, {
        id: crypto.randomUUID(),
        conditions: [{ field: '', operator: 'equals', value: '' }],
        logicalOperator: 'AND',
      }],
    });
  };

  // Update rule
  const updateRule = (index: number, updated: SegmentRule) => {
    const rules = [...formData.rules];
    rules[index] = updated;
    setFormData({ ...formData, rules });
  };

  // Remove rule
  const removeRule = (index: number) => {
    if (formData.rules.length > 1) {
      setFormData({
        ...formData,
        rules: formData.rules.filter((_, i) => i !== index),
      });
    }
  };

  // Save segment
  // View the customers belonging to a segment.
  const MEMBERS_PAGE_SIZE = 25;
  const loadMembers = async (segmentId: string, page: number) => {
    try {
      setMembersLoading(true);
      const data = await customerService.getSegmentMembers(segmentId, {
        limit: MEMBERS_PAGE_SIZE,
        offset: page * MEMBERS_PAGE_SIZE,
      });
      setMembers(data.customers || []);
      setMembersTotal(data.total || 0);
      setMembersPage(page);
    } catch (error) {
      console.error('Error loading segment members:', error);
      toast.error('Failed to load segment members');
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleViewMembers = (segment: CustomerSegment) => {
    setMembersSegment(segment);
    setMembers([]);
    setMembersTotal(0);
    // reset the add-customers flow each time the modal opens
    setShowAddCustomers(false);
    setCustomerSearch('');
    setSearchResults([]);
    setSearchTotal(0);
    setSearchPage(1);
    setSelectedToAdd(new Set());
    loadMembers(segment.id, 0);
  };

  // ---- Static-segment membership: add / remove customers ----
  const handleAddSelected = async () => {
    if (!membersSegment || selectedToAdd.size === 0) return;
    try {
      setIsMutatingMembers(true);
      const res = await customerService.addCustomersToSegment(membersSegment.id, Array.from(selectedToAdd));
      const added = res?.added ?? selectedToAdd.size;
      toast.success(
        added === 0
          ? 'Those customers are already in this segment'
          : `Added ${added} ${added === 1 ? 'customer' : 'customers'}`,
      );
      setSelectedToAdd(new Set());
      setCustomerSearch('');
      setSearchResults([]);
      setShowAddCustomers(false);
      await loadMembers(membersSegment.id, 0);
      loadSegments(); // refresh the card's customer count
    } catch (error) {
      console.error('Error adding customers to segment:', error);
      toast.error('Failed to add customers');
    } finally {
      setIsMutatingMembers(false);
    }
  };

  const handleRemoveMember = async (customerId: string) => {
    if (!membersSegment) return;
    try {
      setIsMutatingMembers(true);
      await customerService.removeCustomersFromSegment(membersSegment.id, [customerId]);
      toast.success('Customer removed from segment');
      // stay on the current page unless it just emptied
      const nextPage = members.length === 1 && membersPage > 0 ? membersPage - 1 : membersPage;
      await loadMembers(membersSegment.id, nextPage);
      loadSegments();
    } catch (error) {
      console.error('Error removing customer from segment:', error);
      toast.error('Failed to remove customer');
    } finally {
      setIsMutatingMembers(false);
    }
  };

  // Navigate to the full customer profile (closing the members modal first).
  const goToProfile = (customerId: string) => {
    setMembersSegment(null);
    navigate(`/customers/${customerId}`);
  };

  // Count conditions that are actually usable (a field selected).
  const countValidConditions = () =>
    formData.rules.reduce((n, g) => n + g.conditions.filter((c) => c.field).length, 0);

  // Live preview: how many customers match the current rules (no save).
  const handlePreview = async () => {
    if (countValidConditions() === 0) {
      toast.error('Add at least one rule (choose a field and value) before previewing.');
      return;
    }
    try {
      setIsPreviewing(true);
      const count = await customerService.previewSegment(formData.rules);
      setPreviewCount(count);
    } catch (error) {
      console.error('Error previewing segment:', error);
      toast.error('Failed to preview match count');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Segment name is required');
      return;
    }
    // A dynamic segment needs at least one real condition — otherwise it would
    // match every customer (the "105 for any rule" problem).
    if (formData.type === 'dynamic' && countValidConditions() === 0) {
      toast.error('Add at least one rule (choose a field and value) — a dynamic segment with no rules matches no customers.');
      return;
    }

    try {
      setIsSaving(true);

      if (editingSegment) {
        await customerService.updateSegment(editingSegment.id, {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          color: formData.color,
          rules: formData.rules,
        });
        toast.success('Segment updated successfully');
      } else {
        await customerService.createSegment({
          name: formData.name,
          description: formData.description,
          type: formData.type,
          color: formData.color,
          rules: formData.rules,
        });
        toast.success('Segment created successfully');
      }

      setShowModal(false);
      loadSegments();
    } catch (error) {
      console.error('Error saving segment:', error);
      toast.error('Failed to save segment');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete segment
  const handleConfirmDelete = async () => {
    if (!deletingSegment) return;
    try {
      setIsDeleting(true);
      await customerService.deleteSegment(deletingSegment.id);
      toast.success('Segment deleted successfully');
      setDeletingSegment(null);
      loadSegments();
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Failed to delete segment');
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle segment active status
  const handleToggleActive = async (segment: CustomerSegment) => {
    try {
      await customerService.updateSegment(segment.id, {
        isActive: !segment.isActive,
      });
      toast.success(`Segment ${segment.isActive ? 'deactivated' : 'activated'}`);
      loadSegments();
    } catch (error) {
      console.error('Error toggling segment:', error);
      toast.error('Failed to update segment');
    }
  };

  // Get color classes
  const getColorClasses = (color?: string) => {
    const colorConfig = SEGMENT_COLORS.find(c => c.value === color) || SEGMENT_COLORS[0];
    return colorConfig;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Customer Segments</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Create dynamic or static segments to group customers
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Segment
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search segments..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          <ThemedSelect
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as typeof filterType)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="dynamic">Dynamic</option>
            <option value="static">Static</option>
          </ThemedSelect>
        </div>
      </div>

      {/* Segments List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filteredSegments.length === 0 ? (
        <Card className="p-8 text-center">
          <Users className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No segments found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {searchQuery
              ? 'Try adjusting your search criteria'
              : 'Create your first segment to organize customers'}
          </p>
          {!searchQuery && (
            <Button onClick={handleCreate} variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Create Segment
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredSegments.map((segment) => {
            const colorClasses = getColorClasses(segment.color);
            return (
              <Card key={segment.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div
                    className="flex items-start gap-3 flex-1 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleViewMembers(segment)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleViewMembers(segment); }}
                    title="View members"
                  >
                    <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
                      <Users className={`h-5 w-5 ${colorClasses.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-600">{segment.name}</h3>
                        <Badge variant={segment.type === 'dynamic' ? 'info' : 'default'}>
                          {segment.type}
                        </Badge>
                        {!segment.isActive && (
                          <Badge variant="warning">Inactive</Badge>
                        )}
                      </div>
                      {segment.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{segment.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {segment.customerCount.toLocaleString()} customers
                        </span>
                        <span>
                          Created {new Date(segment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleToggleActive(segment)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-500"
                      title={segment.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {segment.isActive ? (
                        <ToggleRight className="h-5 w-5 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDuplicate(segment)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-500"
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(segment)}
                      className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors dark:text-gray-500"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setDeletingSegment(segment)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors dark:text-gray-500"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingSegment ? 'Edit Segment' : 'Create Segment'}
        size="lg"
      >
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Segment Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., High-value customers"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe what this segment represents..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type
              </label>
              <ThemedSelect
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as 'dynamic' | 'static' })}
                disabled={!!editingSegment}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="dynamic">Dynamic (auto-update based on rules)</option>
                <option value="static">Static (manually managed)</option>
              </ThemedSelect>
              {editingSegment && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Type can't be changed after creation.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Color
              </label>
              <div className="flex items-center gap-2">
                {SEGMENT_COLORS.map(color => (
                  <button
                    key={color.value}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-8 h-8 rounded-full ${color.bg} ${color.border} border-2 flex items-center justify-center transition-transform ${
                      formData.color === color.value ? 'scale-110 ring-2 ring-offset-2 ring-primary-500' : 'hover:scale-105'
                    }`}
                    title={color.label}
                  >
                    {formData.color === color.value && (
                      <Check className={`h-4 w-4 ${color.text}`} />
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Rules Builder (only for dynamic segments) */}
          {formData.type === 'dynamic' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Segment Rules
                </label>
                <div className="flex items-center gap-3">
                  {previewCount !== null && !isPreviewing && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {previewCount.toLocaleString()} customers match
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={handlePreview}
                    disabled={isPreviewing}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                    title="Count how many customers match these rules"
                  >
                    {isPreviewing ? (
                      <Spinner size="sm" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                    Preview count
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {formData.rules.map((rule, index) => (
                  <div key={rule.id}>
                    {index > 0 && (
                      <div className="flex items-center gap-2 my-3">
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase px-2">OR</span>
                        <div className="flex-1 border-t border-gray-300 dark:border-gray-600" />
                      </div>
                    )}
                    <RuleGroup
                      rule={rule}
                      onChange={(updated) => updateRule(index, updated)}
                      onRemove={() => removeRule(index)}
                      canRemove={formData.rules.length > 1}
                      refOptions={refOptions}
                    />
                  </div>
                ))}

                <button
                  onClick={addRuleGroup}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Plus className="h-4 w-4" />
                  Add rule group (OR)
                </button>
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Customers matching <strong>all conditions within a group</strong> (AND) will be included.
                  Multiple groups are combined with <strong>OR</strong> logic.
                </p>
              </div>
            </div>
          )}

          {formData.type === 'static' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Static segments are manually managed. After creating this segment,
                you can add customers to it from the customer list or individual customer profiles.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {editingSegment ? 'Save Changes' : 'Create Segment'}
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Members Modal — customers belonging to a segment */}
      <Modal
        isOpen={!!membersSegment}
        onClose={() => setMembersSegment(null)}
        title={membersSegment ? `Members — ${membersSegment.name}` : 'Members'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {membersTotal.toLocaleString()} {membersTotal === 1 ? 'customer' : 'customers'} in this segment
            </p>
            {membersSegment?.type === 'static' && (
              <Button size="sm" variant="outline" onClick={() => setShowAddCustomers((v) => !v)}>
                <Plus className="h-4 w-4 mr-1" />
                Add customers
              </Button>
            )}
          </div>

          {/* Add-customers picker (static segments only) */}
          {membersSegment?.type === 'static' && showAddCustomers && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 space-y-2 bg-gray-50 dark:bg-gray-800/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  autoFocus
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  placeholder="Search customers by name or email…"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {searching ? (
                  <div className="flex items-center justify-center py-6"><Spinner size="sm" /></div>
                ) : (
                  (() => {
                    // Hide already-members: the loaded set (up to 100) plus the
                    // current page as a fallback for very large segments.
                    const candidates = searchResults.filter(
                      (c) => !existingMemberIds.has(c.id) && !members.some((m) => m.id === c.id),
                    );
                    if (candidates.length === 0) {
                      return <p className="py-6 text-center text-xs text-gray-500 dark:text-gray-400">No matching customers.</p>;
                    }
                    return candidates.map((c) => (
                      <label key={c.id} className="flex items-center gap-3 px-2 py-2 cursor-pointer hover:bg-white dark:hover:bg-gray-700/50 rounded">
                        <input
                          type="checkbox"
                          checked={selectedToAdd.has(c.id)}
                          onChange={(e) => setSelectedToAdd((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            return next;
                          })}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="min-w-0">
                          <span className="block text-sm text-gray-900 dark:text-white truncate">{c.name || '—'}</span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{c.email}</span>
                        </span>
                      </label>
                    ));
                  })()
                )}
              </div>
              {searchResults.length < searchTotal && (
                <div className="flex items-center justify-center pt-1">
                  <button
                    type="button"
                    onClick={loadMoreCustomers}
                    disabled={loadingMore}
                    className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {loadingMore && <Spinner size="sm" />}
                    Load more ({searchResults.length} of {searchTotal})
                  </button>
                </div>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => { setShowAddCustomers(false); setSelectedToAdd(new Set()); }}>
                  Cancel
                </Button>
                <Button size="sm" disabled={selectedToAdd.size === 0 || isMutatingMembers} onClick={handleAddSelected}>
                  {isMutatingMembers ? <Spinner size="sm" className="mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                  Add {selectedToAdd.size > 0 ? selectedToAdd.size : ''}
                </Button>
              </div>
            </div>
          )}

          {membersLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : members.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No customers in this segment yet.
              </p>
            </div>
          ) : (
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-[50vh] overflow-y-auto">
              {members.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-start justify-between gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors"
                  role="button"
                  tabIndex={0}
                  onClick={() => goToProfile(customer.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter') goToProfile(customer.id); }}
                  title="Open customer profile"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    {customer.avatarUrl ? (
                      <img src={customer.avatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300">
                        {(customer.name || customer.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {customer.name || '—'}
                        </p>
                        <Badge variant={customer.status === 'active' ? 'success' : customer.status === 'blocked' ? 'error' : 'default'}>
                          {customer.status}
                        </Badge>
                        {customer.source && (
                          <Badge variant="default">{customer.source}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{customer.email}</p>
                      {/* Detailed info */}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {customer.phone && (
                          <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{customer.phone}</span>
                        )}
                        {customer.organization?.name && (
                          <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{customer.organization.name}</span>
                        )}
                        {customer.tags?.length > 0 && (
                          <span className="flex items-center gap-1"><Tag className="h-3 w-3" />{customer.tags.join(', ')}</span>
                        )}
                        <span>Since {new Date(customer.createdAt).toLocaleDateString()}</span>
                        {customer.lastSeenAt && (
                          <span>Last seen {new Date(customer.lastSeenAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); goToProfile(customer.id); }}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      View profile
                    </button>
                    {membersSegment?.type === 'static' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveMember(customer.id); }}
                        disabled={isMutatingMembers}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
                        title="Remove from segment"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {membersTotal > MEMBERS_PAGE_SIZE && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Showing {membersPage * MEMBERS_PAGE_SIZE + 1}–
                {Math.min((membersPage + 1) * MEMBERS_PAGE_SIZE, membersTotal)} of {membersTotal.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={membersPage === 0 || membersLoading}
                  onClick={() => membersSegment && loadMembers(membersSegment.id, membersPage - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(membersPage + 1) * MEMBERS_PAGE_SIZE >= membersTotal || membersLoading}
                  onClick={() => membersSegment && loadMembers(membersSegment.id, membersPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        isOpen={!!deletingSegment}
        onClose={() => !isDeleting && setDeletingSegment(null)}
        title="Delete segment"
        size="sm"
      >
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-200">
                Are you sure you want to delete{' '}
                <span className="font-semibold">{deletingSegment?.name}</span>?
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                This can't be undone. The segment{deletingSegment?.type === 'static' ? ' and its member list' : ''} will be removed.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => setDeletingSegment(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Deleting…
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
